import { now as serverNow } from '~/models/clock'
import { startOfTodayJst } from '~/models/dateTime'
import {
  bool,
  defineFilterCodec,
  isoDate,
  listOf,
  oneOf,
} from '~/models/filterCodec'
import { AREA_CODE_OPTIONS } from '~/pages/ProcurementPlanList/constants'
import { type PlanType } from '~/pages/ProcurementPlanList/models/classifyPlanType'
import {
  PLAN_LIST_STATUS_FILTER_OPTIONS,
  type PlanListStatusFilter,
} from '~/pages/ProcurementPlanList/models/planListStatusFilter'

/** 種別フィルタの選択値。'all' は絞り込みなし (procurement-plan-list-type-filter 設計 §5)。 */
export type TypeFilterValue = 'all' | PlanType

/**
 * 需要調達計画一覧のフィルタバー state (in-memory 形状)。画面遷移・再訪をまたいで
 * 保持する対象 (issue #6366 / AC-SC-031)。
 *
 * 対象は FilterToolbar 上の 5 フィルタ (対象日 / エリア / 種別 / 最新のみ / ステータス絞り込み、
 * AC-PPL-032) に限る。
 * 計画期間タブ (periodTab) は FilterToolbar 外の別軸 UI かつ「URL / localStorage への
 * 永続化はしない」(procurement-plan-list-period-tab 設計 Decision #7) のため含めない。
 */
export type ProcurementPlanListFilters = {
  targetDate: Date
  areaFilter: string[]
  typeFilter: TypeFilterValue
  latestOnly: boolean
  statusFilter: PlanListStatusFilter
}

/**
 * フィルタ既定値。対象日は **JST の「本日」**、他はフィルタなし相当。
 *
 * 計画断面 (当日 / 翌日 / 翌々日) のタブを撤去して 1 つの一覧に統合した際、実装は
 * 初期値を「明日」 (`tomorrowDate`、 procurement-plan-list-three-section 設計 AC-04)
 * にしていたが、 spec §3 フィルタ項目表は「本日」 のままだった。 2026-09-10 の PdM
 * 判断で**本日に確定**し、 AC-PPL-033 として明文化された (PR #7732)。
 *
 * 理由は「今日」 ボタン (AC-PPL-015) の戻り先と初期値が一致すること。 明日を初期値に
 * すると、 画面を開いた直後に「今日」 を押すと表示が動く (= 初期値と「今日」 が別の日を
 * 指す) 状態だった。 断面タブ撤去の前後で挙動が変わらない、 という点も判断に入っている。
 *
 * `now` を注入可能にするのは 2 つの時計の混在を避けるため: 既定値を ambient
 * 時計 (`serverNow()`) で導出しつつ復元判定に注入 `now` を使うと、テストが実行時の
 * システム日付に依存して振る舞い不変でも落ちる。
 */
export function defaultProcurementPlanListFilters(
  now: Date = serverNow(),
): ProcurementPlanListFilters {
  return {
    targetDate: startOfTodayJst(now),
    areaFilter: [],
    typeFilter: 'all',
    latestOnly: true,
    statusFilter: 'all',
  }
}

const TYPE_FILTER_VALUES: readonly TypeFilterValue[] = ['all', 'bg', 'retail']

const AREA_VALUES = AREA_CODE_OPTIONS.map((opt) => opt.value)

/**
 * 保存形式は `{ targetDate: 'YYYY-MM-DD', areaFilter, typeFilter, latestOnly, statusFilter }`。
 * 壊れた値・stale enum・未知フィールドは項目単位で既定へ落ちる (deploy skew /
 * 手書き改変を crash させない)。
 *
 * 対象日は AC-SC-031「再訪時に前回の選択状態を復元する」に従い**無条件に復元**する
 * (過去日ナビゲーションは正規サポート、procurement-plan-list.md AC-PPL-014)。
 */
export const procurementPlanListFiltersCodec =
  defineFilterCodec<ProcurementPlanListFilters>({
    defaults: defaultProcurementPlanListFilters,
    fields: {
      targetDate: isoDate(),
      areaFilter: listOf(oneOf(AREA_VALUES)),
      typeFilter: oneOf(TYPE_FILTER_VALUES),
      latestOnly: bool(),
      statusFilter: oneOf(PLAN_LIST_STATUS_FILTER_OPTIONS),
    },
  })
